import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';

// Actions
import { fetchCmsData } from '../../cms/cmsActions';
import { onboardingStepPassed } from '../onboarding/onboardingActions';

// Selectors
import {
    templatePickerVisibleSelector,
    getOnboardTemplateLandingPage,
    getOnboardSelectTemplate,
    getOnboardHideTemplatePicker,
} from './boardTemplatePickerSelectors';
import { getCurrentUserSegment, getCurrentUserSegments } from '../../user/currentUserSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Components
import useUpdateEffect from '../../utils/react/useUpdateEffect';
import BoardTemplatePickerCopyLoader from './loader/BoardTemplatePickerCopyLoader';
import BoardTemplatePickerPublicParentPopup from './publicTemplatePopup/BoardTemplatePickerPublicParentPopup';
import BoardTemplatePickerPopup from './BoardTemplatePickerPopup';

// Utils
import { isTemplatePublicParentBoard } from '../../../common/templates/templateUtil';
import usePrevious from '../../utils/react/usePrevious';
import { isPlatformPhoneOrMobileMode } from '../../platform/utils/platformDetailsUtils';

// Constants
import { CmsEntities } from '../../cms/cmsConstants';

// Styles
import './BoardTemplatePickerPopup.scss';

const mapStateToProps = createStructuredSelector({
    currentUserSegment: getCurrentUserSegment,
    currentUserSegments_old: getCurrentUserSegments,
    templatePickerVisible: templatePickerVisibleSelector,
    onboardingLandingPage: getOnboardTemplateLandingPage,
    onboardingSelectTemplate: getOnboardSelectTemplate,
    onboardingHidePicker: getOnboardHideTemplatePicker,
    platformDetails: getPlatformDetailsSelector,
});

const mapDispatchToProps = (dispatch) => ({
    fetchTemplateCMSData: ({ segments, landingPage }) =>
        dispatch(
            fetchCmsData(
                CmsEntities.TEMPLATES,
                // Force fetching, because we're already preventing unnecessary fetches
                // in the useEffect
                true,
            )({
                segments,
                landingPage: landingPage ? landingPage : undefined,
            }),
        ),
    pickerDismissedCallback: () => dispatch(onboardingStepPassed('template-picker')),
});

const useCMSTemplateFetching = ({
    currentUserSegment,
    currentUserSegments_old,
    onboardingLandingPage,
    fetchTemplateCMSData,
}) => {
    const prevUserSegment = usePrevious(currentUserSegment);
    const prevUserSegments_old = usePrevious(currentUserSegments_old);
    const prevOnboardingLandingPage = usePrevious(onboardingLandingPage);

    useEffect(() => {
        const hasSegmentData = !!currentUserSegment || !!currentUserSegments_old;
        const hasChangedSegmentProps =
            prevUserSegment !== currentUserSegment ||
            prevUserSegments_old !== currentUserSegments_old ||
            prevOnboardingLandingPage !== onboardingLandingPage;

        const shouldFetchCMSData = hasSegmentData && hasChangedSegmentProps;
        if (!shouldFetchCMSData) return;

        fetchTemplateCMSData({
            segments: currentUserSegment ? [currentUserSegment] : currentUserSegments_old.toArray(),
            landingPage: onboardingLandingPage,
        });
    }, [currentUserSegments_old, currentUserSegment, onboardingLandingPage]);
};

const BoardTemplatePickerRoot = ({
    currentBoardId,
    currentBoard,
    templatePickerVisible,
    currentUserSegment,
    currentUserSegments_old,
    onboardingLandingPage,
    fetchTemplateCMSData,
    pickerDismissedCallback,
    platformDetails,
    ...rest
}) => {
    const [isCopyingTemplate, setIsCopyingTemplate] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [popupDismissed, setPopupDismissed] = useState(false);
    const [keepTemplateContentChecked, setKeepTemplateContentChecked] = useState(false);
    const [pickerReady, setPickerReady] = useState(templatePickerVisible);

    const dismissTemplatePicker = () => {
        pickerDismissedCallback();
        setPopupDismissed(true);
    };

    // Delay the display of the picker to prevent it flashing when dropping
    // an element from another board
    useUpdateEffect(() => {
        let timeoutId = null;

        if (!templatePickerVisible) {
            setPickerReady(false);
            return;
        }

        timeoutId = setTimeout(() => {
            setPickerReady(true);
            timeoutId = null;
        }, 50);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [templatePickerVisible]);

    useEffect(() => {
        setPopupDismissed(false);
    }, [currentBoardId]);

    useCMSTemplateFetching({
        currentUserSegment,
        currentUserSegments_old,
        onboardingLandingPage,
        fetchTemplateCMSData,
    });

    const showPicker =
        templatePickerVisible &&
        pickerReady &&
        !isCopyingTemplate &&
        !popupDismissed &&
        !isPlatformPhoneOrMobileMode(platformDetails); // For mini milanote v1 we are just hiding the template picker

    // shown to admins to make the board id to add to Prismic obvious
    if (isTemplatePublicParentBoard(currentBoard)) {
        return <BoardTemplatePickerPublicParentPopup currentBoard={currentBoard} />;
    }

    return (
        <Fragment>
            {isCopyingTemplate && (
                <BoardTemplatePickerCopyLoader
                    selectedTemplateId={selectedTemplateId}
                    keepTemplateContentChecked={keepTemplateContentChecked}
                />
            )}

            <div className="BoardTemplatePickerPopup">
                <CSSTransitionGroup
                    transitionName="fade"
                    transitionAppear
                    transitionAppearTimeout={200}
                    transitionEnterTimeout={200}
                    transitionLeaveTimeout={200}
                >
                    {showPicker && (
                        <BoardTemplatePickerPopup
                            {...rest}
                            currentBoard={currentBoard}
                            isCopyingTemplate={isCopyingTemplate}
                            keepTemplateContentChecked={keepTemplateContentChecked}
                            setKeepTemplateContentChecked={setKeepTemplateContentChecked}
                            dismissTemplatePicker={dismissTemplatePicker}
                            setIsCopyingTemplate={setIsCopyingTemplate}
                            selectedTemplateId={selectedTemplateId}
                            setSelectedTemplateId={setSelectedTemplateId}
                        />
                    )}
                </CSSTransitionGroup>
            </div>
        </Fragment>
    );
};

BoardTemplatePickerRoot.propTypes = {
    templatePickerVisible: PropTypes.bool,
    onboardingLandingPage: PropTypes.string,
    onboardingSelectTemplate: PropTypes.string,
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    currentUserSegment: PropTypes.string,
    currentUserSegments_old: PropTypes.object,
    fetchTemplateCMSData: PropTypes.func,
    pickerDismissedCallback: PropTypes.func,
    platformDetails: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(BoardTemplatePickerRoot);
